import {
  Button,
  ButtonGroup,
  DatePickerNative,
  Dropdown,
  FormControl,
  type IModalProps,
  Input,
  Modal,
  Stack,
  Text,
  useToast,
} from '@lego/klik-ui';
import { CronExpressionParser } from 'cron-parser';
import { useState } from 'react';
import { useMutation } from '@/hooks/api';
import type { RolloutWindowResponse, RolloutWindowType } from '@/hooks/rollout_windows';
import { ToastOptions } from '@/utility/ToastOptions';
import { CronBuilder } from '../common/CronBuilder';

export type AddRolloutWindowDialogProps = {
  deviceId: number;
  workspaceId: number;
  onSuccess: () => void;
};

export const AddRolloutWindowDialog = ({
  deviceId,
  workspaceId,
  onSuccess,
  isOpen,
  onClose,
}: AddRolloutWindowDialogProps & Omit<IModalProps, 'children'>) => {
  const [startTime, setStartTime] = useState<string | undefined>(undefined);
  const [cronExpression, setCronExpression] = useState<string | undefined>(undefined);
  const [durationInSeconds, setDurationInSeconds] = useState<number | undefined>(undefined);
  const [type, setType] = useState<RolloutWindowType>('APPLICATION');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const addRolloutWindow = useMutation<
    RolloutWindowResponse,
    {
      deviceId: number;
      workspaceId: number;
      payload:
        | {
            startTime: string;
            durationInSeconds: number;
            type: RolloutWindowType;
          }
        | {
            cronExpression: string;
            durationInSeconds: number;
            type: RolloutWindowType;
          };
    }
  >(
    'device-add-rollout-window',
    async (client, { deviceId, workspaceId, payload }) =>
      await client.v1.workspaces[':workspaceId'].devices[':deviceId']['rollout-windows'].$post({
        param: { workspaceId: workspaceId.toString(), deviceId: deviceId.toString() },
        json: payload,
      })
  );

  const resetForm = () => {
    setStartTime(undefined);
    setCronExpression(undefined);
    setDurationInSeconds(undefined);
    setType('APPLICATION');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getValidatedPayloadSchedule = () => {
    if (startTime) {
      return { startTime: new Date(startTime).toISOString() };
    }
    if (!cronExpression) return null;
    try {
      CronExpressionParser.parse(cronExpression);
      return { cronExpression };
    } catch (error) {
      console.error('Invalid cron expression:', error);
      toast(ToastOptions.error('Error', 'Invalid cron expression. Please check the format.'));
      return null;
    }
  };

  const handleAddRolloutWindow = async () => {
    if (!durationInSeconds) {
      console.error('Please fill in all required fields.');
      return toast(
        ToastOptions.error(
          'Error',
          'Please fill in all required fields: Start Time or Cron Expression and the duration.'
        )
      );
    }

    const schedule = getValidatedPayloadSchedule();
    if (!schedule) {
      if (!startTime && !cronExpression) {
        console.error('Please fill in all required fields.');
        toast(
          ToastOptions.error(
            'Error',
            'Please fill in all required fields: Start Time or Cron Expression and the duration.'
          )
        );
      }
      return;
    }

    setIsLoading(true);
    try {
      await addRolloutWindow({
        deviceId: deviceId,
        payload: {
          ...schedule,
          durationInSeconds: durationInSeconds!,
          type,
        },
        workspaceId: workspaceId,
      });
      onSuccess();
      handleClose();
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal hideIcon={true} isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header fontSize="xl">Add Rollout Window</Modal.Header>
        <Modal.CloseButton />

        <Modal.Body>
          <Text fontSize="sm" marginBottom={4}>
            Choose either a one-time rollout window (Start Time) or a recurring schedule (Cron
            Expression).
          </Text>
          <Stack direction="column" spacing={4}>
            <FormControl>
              <FormControl.Label>One-time Start Time</FormControl.Label>
              <DatePickerNative
                label=""
                onChange={setStartTime}
                type="datetime-local"
                value={startTime}
                width="100%"
              />
              <FormControl.HelperText>
                For a one-time rollout window (leave empty if using recurring schedule)
              </FormControl.HelperText>
            </FormControl>

            <FormControl>
              <FormControl.Label>Recurring Schedule (Cron)</FormControl.Label>
              <CronBuilder onChange={setCronExpression} value={cronExpression} />
              <FormControl.HelperText>
                Build a recurring schedule using the visual editor or enter a cron expression
                manually
              </FormControl.HelperText>
            </FormControl>

            <FormControl isRequired>
              <FormControl.Label>Duration (seconds)</FormControl.Label>
              <Input
                data-testid="duration-input"
                onChange={({ target: { value } }) =>
                  setDurationInSeconds(value ? Number(value) : undefined)
                }
                placeholder="Duration in seconds"
                type="number"
                value={durationInSeconds || ''}
              />
              <FormControl.HelperText>
                How long the rollout window stays open
              </FormControl.HelperText>
            </FormControl>

            <FormControl>
              <FormControl.Label>Allowed Update Type</FormControl.Label>
              <Dropdown
                onChange={(value: string) => setType(value as RolloutWindowType)}
                value={[type]}
                width="100%"
              >
                <Dropdown.Option value="APPLICATION">Application only</Dropdown.Option>
                <Dropdown.Option value="APPLICATION_AND_OS">Application and OS</Dropdown.Option>
              </Dropdown>
            </FormControl>
          </Stack>
        </Modal.Body>

        <Modal.Footer>
          <ButtonGroup>
            <Button isLoading={isLoading} onClick={handleAddRolloutWindow}>
              Add
            </Button>
            <Button isDisabled={isLoading} onClick={handleClose} variant="outline">
              Cancel
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
